import React, {
  FC, useCallback, useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { setChatAlert } from 'models/twilio';
import {
  usePopupState,
  bindTrigger,
  bindPopover,
} from 'material-ui-popup-state/hooks';
import { useTranslation } from 'react-i18next';
import TRANSLATIONS from 'components/twilio/translations';
import { Backdrop, ButtonBase, Popover } from '@material-ui/core';
import styled from 'styled-components';
import { ExitToAppRounded } from '@material-ui/icons';
import { leaveGroupChat } from 'network/twilioRequests';
import ColoredMaterialIcon from './styled/ColoredMaterialIcon';
import { COLORS } from '../constants';
import ChatIconButton from './styled/ChatIconButton';
import useCurrentAttendee from '../hooks/useCurrentAttendee';
import MessageLoadingIndicator from './styled/MessageLoadingIndicator';

const StyledButtonBase = styled(ButtonBase)`
  width: 100%;
  padding: 12px 16px;
  font-family: Lato, san-serif;
  color: ${COLORS.TEXT_DARK};
`;

const StyledBackdrop = styled(Backdrop)`
  z-index: 1300;
  position: absolute;
  background-color: rgba(0, 0, 0, 0.2);
`;

export interface LeaveGroupButtonProps {
  conversationSid: string;
  isPublicChatroom?: boolean;
}

const LeaveGroupButton: FC<LeaveGroupButtonProps> = ({ conversationSid, isPublicChatroom }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [processingLeave, setProcessing] = useState(false);
  const { uuid } = useCurrentAttendee();
  const popupState = usePopupState({
    variant: 'popover',
    popupId: `LeaveGroupButton-${conversationSid}`,
  });

  const onLeaveClick = useCallback(async () => {
    popupState.close();
    setProcessing(true);
    const success = await leaveGroupChat(uuid, conversationSid);
    if (success) dispatch(setChatAlert({ message: TRANSLATIONS.COMMON.LEAVE_GROUP_SUCCESS[1], translationKey: TRANSLATIONS.COMMON.LEAVE_GROUP_SUCCESS[0], severity: 'success' }));
    else {
      dispatch(setChatAlert({ message: TRANSLATIONS.ERRORS.LEAVE_FAIL[1], translationKey: TRANSLATIONS.ERRORS.LEAVE_FAIL[0], severity: 'error' }));
      setProcessing(false);
    }
  }, [popupState, dispatch, uuid, conversationSid]);

  return (
    <>
      <ChatIconButton id="chat-leave-button" {...bindTrigger(popupState)}>
        <ColoredMaterialIcon Icon={ExitToAppRounded} />
      </ChatIconButton>
      <Popover
        {...bindPopover(popupState)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <StyledButtonBase onClick={onLeaveClick}>
          {t(...TRANSLATIONS.COMMON.LEAVE_CHAT)}
        </StyledButtonBase>
      </Popover>
      <StyledBackdrop open={processingLeave}>
        <MessageLoadingIndicator />
      </StyledBackdrop>
    </>
  );
};

export default LeaveGroupButton;
